import * as flights from '../flights';
import * as filters from './filters';
import * as utils from '../utils';

export class FlightFilterService {
    public sort: string;

    public airlines: filters.AirlinesFilter;
    public baggage: filters.BaggageFilter;
    public changeRefund: filters.ChangeRefundFilter;
    public flightDuration: filters.FlightDurationFilter;
    public airports: filters.AirportsFilter;
    public departureArrival: filters.DepartureArrivalFilter;
    public flightKind: filters.FlightKindFilter;
    public price: filters.PriceFilter;
    public stopCount: filters.StopCountFilter;
    public stopDuration: filters.StopDurationFilter;

    constructor(flights: flights.FlightModel[], urlFilters: string) {
        this.sort = 'price';

        this.airlines = new filters.AirlinesFilter(flights);
        this.baggage = new filters.BaggageFilter();
        this.changeRefund = new filters.ChangeRefundFilter();
        this.flightDuration = new filters.FlightDurationFilter(flights);
        this.airports = new filters.AirportsFilter(flights);
        this.departureArrival = new filters.DepartureArrivalFilter(flights);
        this.flightKind = new filters.FlightKindFilter(flights);
        this.price = new filters.PriceFilter(flights);
        this.stopCount = new filters.StopCountFilter();
        this.stopDuration = new filters.StopDurationFilter(flights);

        if (urlFilters) {
            const parsed = urlFilters.split('-');

            parsed.forEach(f => {
                if (f.length == 2) {
                    // It's airline
                    this.airlines.items.forEach((item) => {
                        if (item.airline.iataCode === urlFilters) {
                            item.checked = true;
                        }
                    });
                } else {
                    if (f == 'd') {
                        this.stopCount.none = true;
                    } else if (f == 'b') {
                        this.baggage.with = true;
                    }
                }
            });
        }
    }

    get touched(): boolean {
        return this.airlines.touched
            || this.baggage.touched
            || this.changeRefund.touched
            || this.flightDuration.touched
            || this.airports.touched
            || this.departureArrival.touched
            || this.flightKind.touched
            || this.price.touched
            || this.stopCount.touched
            || this.stopDuration.touched;
    }

    public filter(
        flights: flights.FlightFilterModel[],
        flightsToView: { [uniqueId: string]: flights.FlightModel },
        filters: flights.FlightFilterService,
        allowGroupping: boolean
    ): flights.FlightFilterResult {
        // Filter
        this.filterFlights(flights, flightsToView);

        // Reset grouping
        flights.forEach((flight) => {
            flight.parentId = '';
            flight.childCount = 0;
        });

        // Apply grouping
        // if (filters.sort === 'price' && allowGroupping) {
        //     this.groupFlights(flights, flightsToView);
        // }

        // Sort
        this.sortFlights(flights, flightsToView);

        // Count results
        const result: flights.FlightFilterResult = {
            flightsVisibleCount: 0,
            flightsVisibleParentCount: 0
        };

        flights.forEach((f) => {
            if (f.visible) {
                result.flightsVisibleCount++;
                if (!f.parentId) {
                    result.flightsVisibleParentCount++;
                }
            }
        });

        return result;
    }

    clear() {
        this.airlines.clear();
        this.airports.clear();
        this.baggage.clear();
        this.changeRefund.clear();
        this.flightDuration.clear();
        this.departureArrival.clear();
        this.flightKind.clear();
        this.price.clear();
        this.stopCount.clear();
        this.stopDuration.clear();
    }

    private filterFlights(flights: flights.FlightFilterModel[], flightsToView: { [uniqueId: string]: flights.FlightModel }) {
        flights.forEach((f) => {
            const flight = flightsToView[f.uniqueId];

            if (!this.touched
                || (this.touched
                    && (this.stopCount.check(flight)
                        && this.baggage.check(flight)
                        && this.changeRefund.check(flight)
                        && this.stopDuration.check(flight)
                        && this.airlines.check(flight)
                        && this.flightDuration.check(flight)
                        && this.airports.check(flight)
                        && this.departureArrival.check(flight)
                        && this.flightKind.check(flight)
                        && this.price.check(flight)))) {
                f.visible = true;

                return;
            }

            f.visible = false;
        });
    }

    private groupFlights(flightFilters: flights.FlightFilterModel[], flights: { [uniqueId: string]: flights.FlightModel }) {
        flightFilters.forEach((potentialParentFlightFilter: flights.FlightFilterModel) => {
            const potentialParentFlight = flights[potentialParentFlightFilter.uniqueId];

            if (potentialParentFlightFilter.visible && !potentialParentFlightFilter.parentId) {
                const childrenFlightFilters = flightFilters.filter((flightFilter: flights.FlightFilterModel) => {
                    const potentialChildFlight = flights[flightFilter.uniqueId];

                    return flightFilter.uniqueId !== potentialParentFlightFilter.uniqueId
                        && potentialChildFlight.billingPrice === potentialParentFlight.billingPrice
                        && potentialChildFlight.legs[0].segments[0].airline.iataCode === potentialParentFlight.legs[0].segments[0].airline.iataCode
                        && (potentialChildFlight.legs.length == 1
                            || (potentialChildFlight.legs.length > 1
                                && potentialChildFlight.legs[1].segments[0].airline.iataCode === potentialParentFlight.legs[1].segments[0].airline.iataCode))
                        // There can be different airports so do not group different airports
                        && potentialChildFlight.legs[0].segments[0].departureDestination.iataCode === potentialParentFlight.legs[0].segments[0].departureDestination.iataCode
                        && potentialChildFlight.legs[0].segments[potentialChildFlight.legs[0].segments.length - 1].arrivalDestination.iataCode === potentialParentFlight.legs[0].segments[potentialParentFlight.legs[0].segments.length - 1].arrivalDestination.iataCode;
                });

                if (childrenFlightFilters.length > 0) {
                    childrenFlightFilters.forEach((childFlightFilter: flights.FlightFilterModel) => {
                        childFlightFilter.parentId = potentialParentFlightFilter.uniqueId;
                        potentialParentFlightFilter.childCount++;
                    });
                }
            }
        });
    }

    sumFast(flight: flights.FlightModel) {
        return flight.legs.reduce((total, f) => total + f.durationMinutes, 0) / 60;
    }

    private sortFlights(flightFilters: flights.FlightFilterModel[], flightsToView: { [uniqueId: string]: flights.FlightModel }) {
        switch (this.sort) {
            case 'price':
            {
                flightFilters.sort(
                    utils
                    .firstBy((v1: flights.FlightFilterModel, v2: flights.FlightFilterModel) => { return flightsToView[v1.uniqueId].priority - flightsToView[v2.uniqueId].priority; }, { direction: -1 })
                    .thenBy((v1: flights.FlightFilterModel, v2: flights.FlightFilterModel) => { return flightsToView[v1.uniqueId].billingPrice - flightsToView[v2.uniqueId].billingPrice; })
                    .thenBy((v1: flights.FlightFilterModel, v2: flights.FlightFilterModel) => { return this.sumFast(flightsToView[v1.uniqueId]) - this.sumFast(flightsToView[v2.uniqueId]); })
                );
                break;
            }
            case 'duration':
            {
                flightFilters.sort(
                    utils
                    .firstBy((v1: flights.FlightFilterModel, v2: flights.FlightFilterModel) => { return flightsToView[v1.uniqueId].priority - flightsToView[v2.uniqueId].priority; }, { direction: -1 })
                    .thenBy((v1: flights.FlightFilterModel, v2: flights.FlightFilterModel) => { return this.sumFast(flightsToView[v1.uniqueId]) - this.sumFast(flightsToView[v2.uniqueId]); })
                    .thenBy((v1: flights.FlightFilterModel, v2: flights.FlightFilterModel) => { return flightsToView[v1.uniqueId].billingPrice - flightsToView[v2.uniqueId].billingPrice; })
                );
                break;
            }
            case 'best':
            {   
                let resByTime = flightFilters.sort((a, b) => this.sumFast(flightsToView[a.uniqueId]) - this.sumFast(flightsToView[b.uniqueId])).map((e, i) => ({
                    uid: e.uniqueId,
                    index: i,
                    value: this.sumFast(flightsToView[e.uniqueId]),
                    priority: flightsToView[e.uniqueId].priority
                }));
                
                let resByCost = flightFilters.sort((a, b) => flightsToView[a.uniqueId].billingPrice - flightsToView[b.uniqueId].billingPrice).map((e, i) => ({
                    uid: e.uniqueId,
                    index: i,
                    value: flightsToView[e.uniqueId].billingPrice,
                    priority: flightsToView[e.uniqueId].priority
                }));

                const swap = (json)=>{
                    var ret = {};
                    for(var key in json){
                        ret[json[key].uid] = json[key].rank;
                    }
                    
                    return ret;
                }                    
                
                let setRank = (elems) => {  
                    let prevValue = null;
                    let rankIndex = 1;
                  
                    for (let elem of elems) {
                        
                        if (!prevValue || elem.value == (<any>prevValue).value) {
                            elem.rank = rankIndex;
                        } else {
                            elem.rank = ++rankIndex;
                        }
                        
                        prevValue = elem;
                        
                    }

                    return elems;
                }
                
                let merge = (time, cost) => {
                    let res = {};
                    
                    for (let key in time) {
                        res[key] = parseFloat(cost[key]) + parseFloat(time[key]) * 0.5
                    }
                    return res;
                }
                
                let result = merge(
                    swap(setRank(resByTime)),
                    swap(setRank(resByCost))
                )
                
                flightFilters.sort(
                    utils
                        .firstBy((v1: flights.FlightFilterModel, v2: flights.FlightFilterModel) => { return flightsToView[v1.uniqueId].priority - flightsToView[v2.uniqueId].priority; }, { direction: -1 })
                        .thenBy((v1: flights.FlightFilterModel, v2: flights.FlightFilterModel) => { return (result[v1.uniqueId] - result[v2.uniqueId] )})
                )
                
                break;
            }
            case 'departureDate':
            {
                flightFilters.sort(
                    utils
                        .firstBy((v1: flights.FlightFilterModel, v2: flights.FlightFilterModel) => { return flightsToView[v1.uniqueId].priority - flightsToView[v2.uniqueId].priority; }, { direction: -1 })
                        .thenBy((v1: flights.FlightFilterModel, v2: flights.FlightFilterModel) => { return flightsToView[v1.uniqueId].legs[0].departureDate.unix() - flightsToView[v2.uniqueId].legs[0].departureDate.unix(); })
                );
                break;
            }
            case 'arrivalDate':
            {
                flightFilters.sort(
                    utils
                        .firstBy((v1: flights.FlightFilterModel, v2: flights.FlightFilterModel) => { return flightsToView[v1.uniqueId].priority - flightsToView[v2.uniqueId].priority; }, { direction: -1 })
                        .thenBy((v1: flights.FlightFilterModel, v2: flights.FlightFilterModel) => { return flightsToView[v1.uniqueId].legs[flightsToView[v1.uniqueId].legs.length - 1].arrivalDate.unix() - flightsToView[v2.uniqueId].legs[flightsToView[v1.uniqueId].legs.length - 1].arrivalDate.unix(); })
                );
                break;
            }
        }
    }
}
